import { content } from "core/utils/content";
import { enforceDoubles, enforceEmptyOnZero } from "core/utils/general";
import * as Either from "fp-ts/lib/Either";
/**
 * Returns number of milliseconds based on hours, minutes and seconds.
 */
export function getMs(_a) {
    var hours = _a.hours, minutes = _a.minutes, seconds = _a.seconds;
    if (hours < 0 || minutes < 0 || seconds < 0)
        return 0;
    return hours * 60 * 60 * 1000 + minutes * 60 * 1000 + seconds * 1000;
}
export function isTomorrow(now, date) {
    var tomorrow = new Date(new Date(now).getFullYear(), new Date(now).getMonth(), new Date(now).getDate() + 1);
    return ((tomorrow.getFullYear() === new Date(date).getFullYear() &&
        tomorrow.getMonth() === new Date(date).getMonth() &&
        tomorrow.getDate() === new Date(date).getDate() &&
        3 <= new Date(date).getHours()) ||
        (tomorrow.getFullYear() === new Date(date).getFullYear() &&
            tomorrow.getMonth() === new Date(date).getMonth() &&
            tomorrow.getDate() === new Date(date).getDate() + 1 &&
            3 > new Date(date).getHours()));
}
export function isTheDayAfterTomorrow(now, date) {
    var tomorrow = new Date(new Date(now).getFullYear(), new Date(now).getMonth(), new Date(now).getDate() + 2);
    return ((tomorrow.getFullYear() === new Date(date).getFullYear() &&
        tomorrow.getMonth() === new Date(date).getMonth() &&
        tomorrow.getDate() === new Date(date).getDate() &&
        3 <= new Date(date).getHours()) ||
        (tomorrow.getFullYear() === new Date(date).getFullYear() &&
            tomorrow.getMonth() === new Date(date).getMonth() &&
            tomorrow.getDate() === new Date(date).getDate() + 2 &&
            3 > new Date(date).getHours()));
}
/**
 * When the given deadline is a valid date and in the future, returns remaining milliseconds until the deadline.
 */
export function getDurationFromNow(deadline) {
    var deadlineMs = Date.parse(deadline);
    var now = Date.now();
    if (now > deadlineMs)
        return Either.left(content.deadlineHasPassed);
    return Either.right(deadlineMs - now);
}
/**
 * Given the date in milliseconds, returns the day of the week
 */
export function getWeekDay(date) {
    var day = new Date(date).getDay();
    var time = new Date(date).getHours();
    switch (day) {
        case 0:
            if (time >= 3) {
                return content.weekday.sunday;
            }
            else {
                return content.weekday.saturday;
            }
        case 1:
            if (time >= 3) {
                return content.weekday.monday;
            }
            else {
                return content.weekday.sunday;
            }
        case 2:
            if (time >= 3) {
                return content.weekday.tuesday;
            }
            else {
                return content.weekday.monday;
            }
        case 3:
            if (time >= 3) {
                return content.weekday.wednesday;
            }
            else {
                return content.weekday.tuesday;
            }
        case 4:
            if (time >= 3) {
                return content.weekday.thursday;
            }
            else {
                return content.weekday.wednesday;
            }
        case 5:
            if (time >= 3) {
                return content.weekday.friday;
            }
            else {
                return content.weekday.thursday;
            }
        case 6:
            if (time >= 3) {
                return content.weekday.saturday;
            }
            else {
                return content.weekday.friday;
            }
        default:
            return content.weekday.unknown;
    }
}
/**
 * Formats the given time to 10.45
 */
export function getTime(date) {
    var time = new Date(date);
    var hours = enforceDoubles(time.getHours());
    var minutes = enforceDoubles(time.getMinutes());
    return "".concat(hours, ":").concat(minutes);
}
/**
 * Formats the given time to 10min3
 */
export function getTimeWithMin(date) {
    var time = new Date(date);
    var minutes = time.getMinutes();
    var seconds = enforceEmptyOnZero(time.getSeconds());
    return "".concat(minutes, "min").concat(seconds);
}
/**
 * Formats the given time to 10:03 (minutes:seconds)
 */
export function getDigitalMinSec(date) {
    var time = new Date(date);
    var minutes = enforceDoubles(time.getMinutes());
    var seconds = enforceDoubles(time.getSeconds());
    return "".concat(minutes, ":").concat(seconds);
}
/**
 * Formats milliseconds to 10min3
 */
export function millisToTimeWithMin(millis) {
    var minutes = Math.floor(millis / 60000);
    var seconds = enforceEmptyOnZero((millis % 60000) / 1000);
    return "".concat(minutes, "min").concat(seconds);
}
/**
 * Validates equality of two dates based on day, month and year
 */
export function isSameDay(date1, date2) {
    var day1 = new Date(date1);
    var day2 = new Date(date2);
    return ((day1.getFullYear() === day2.getFullYear() &&
        day1.getMonth() === day2.getMonth() &&
        day1.getDate() === day2.getDate() &&
        3 <= day1.getHours()) ||
        (day1.getFullYear() === day2.getFullYear() &&
            day1.getMonth() === day2.getMonth() &&
            day1.getDate() + 1 === day2.getDate() &&
            3 > day2.getHours()));
}
/**
 * Checks if supplied date is within the duration of the current moment(now)
 */
export function isWithin(duration, date) {
    if (Date.now() > date)
        return false;
    return Date.now() + duration > date;
}
/**
 * Checks if supplied hour is after the threshold hour
 */
export function hourIsAfter(thresholdHour, date) {
    var hour = new Date(date).getHours();
    return hour >= thresholdHour;
}
/**
 * Checks if suplied date is after current moment(now)
 * @TODO maybe we should lower the accuracy of this function.
 * The exact millisecond doesn't matter. And sometimes tests
 * fail because the generated now is a few millisecond behind
 * this now.
 */
export function isFuture(datetime, now) {
    var deadline = Date.parse(datetime);
    return deadline >= (now || Date.now());
}
/**
 * Checks if given date is within the given days.
 */
export function isWithinDays(datetime, days) {
    var diffTime = Date.parse(datetime) - Date.now();
    var diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= days;
}
/**
 * Return date as day and month.
 */
export function getDayMonth(datetime) {
    var date = new Date(datetime);
    var month = content.months[date.getMonth()];
    return "".concat(date.getDate(), " ").concat(month);
}
